import { lazy, useState, Suspense } from "react";
import { Route, Routes } from "react-router";
import Navigation from "./components/Navigation/Navigation";
import UserProfile from "./components/User/UserProfile";
import Breadcrumbs from "./components/Breadcrumbs";
import NotificationsLink from "./components/Notifications/NotificationsLink";
import User from "./pages/users/user/User";
import { useAuth } from "./hooks/useAuth";
import { Permission } from "./components/Auth/permissions";
import Spinner from "./components/common/Spinner";
import AuthError from "./components/Auth/AuthError";
import ErrorBoundary from "./components/Error/ErrorBoundary";
import HelpCenterLink from "./components/HelpCenter/HelpCenterLink";
import NotFound from "./NotFound";
import useMainContentScrollRestoration from "./hooks/useMainContentScrollRestoration";
import Badge from "./components/common/Badge";

const Home = lazy(() => import("./pages/home/Home"));
const Notifications = lazy(() => import("./pages/notifications/Notifications"));
const Policies = lazy(() => import("./pages/policies/Policies"));
const Invoices = lazy(() => import("./pages/invoices/Invoices"));
const Organisations = lazy(() => import("./pages/organisations/Organisations"));
const Organisation = lazy(
	() => import("./pages/organisations/organisation/Organisation")
);
const OrganisationsTreeviewPage = lazy(
	() => import("./pages/organisations/OrganisationsTreeviewPage")
);
const UpdateRequests = lazy(
	() => import("./pages/update_requests/UpdateRequests")
);
const UpdateRequest = lazy(
	() => import("./pages/update_requests/update_request/UpdateRequest")
);
const Users = lazy(() => import("./pages/users/Users"));
const Policy = lazy(() => import("./pages/policies/policy/Policy"));
const Cyberrisk = lazy(() => import("./pages/cyberrisk/Cyberrisk"));
// const Claims = lazy(() => import("./pages/claims/Claims"));
const Circulars = lazy(() => import("./pages/circulars/Circulars"));
const Articles = lazy(() => import("./pages/articles/Articles"));

const withSuspense = (element) => (
	<Suspense fallback={<Spinner centered />}>{element}</Suspense>
);

function App() {
	const { /*user,*/ checkPermissions, isAuthenticated, isLoading, error } =
		useAuth();
	useMainContentScrollRestoration();

	const [isNavigationVisible, setIsNavigationVisible] = useState(false);
	const toggleNavigation = () => setIsNavigationVisible((c) => !c);
	if (isLoading) {
		return <Spinner centered />;
	}
	return (
		<>
			<main className="flex min-h-screen max-w-full overflow-hidden max-h-full">
				<Navigation
					visible={isNavigationVisible}
					setVisibility={setIsNavigationVisible}
				/>
				<section className="flex-1 flex flex-col overflow-hidden">
					<header className="z-[100] print:hidden overflow-x-hidden">
						<div className="flex justify-between items-center bg-la-creme drop-shadow-lg">
							<div className="flex items-center overflow-x-hidden">
								<div className="md:hidden p-4">
									<button
										type="button"
										aria-label="Open navigation"
										className="cursor-pointer text-xl"
										onClick={toggleNavigation}
									>
										&#9776;
									</button>
								</div>

								<Breadcrumbs />
							</div>
							{["local", "dev", "development", "review"].includes(
								ENV
							) && (
								<Badge
									variant="the-brown"
									size="sm"
									className="truncate mx-2"
									title={API_SERVER}
								>
									API: {API_SERVER}
								</Badge>
							)}

							<div className="self-end flex gap-4 items-center bg-footer-header-dark p-4">
								<HelpCenterLink />
								<NotificationsLink />
								<UserProfile />
							</div>
						</div>
					</header>
					<section
						id="main-content"
						className="bg-background flex-1 h-full overflow-y-scroll z-0"
					>
						<ErrorBoundary>
							{!isAuthenticated ? (
								<AuthError error={error} />
							) : (
								<Routes>
									<Route
										path=""
										element={withSuspense(<Home />)}
									/>
									<Route
										path="notifications"
										exact
										element={withSuspense(
											<Notifications />
										)}
									/>
									{checkPermissions("any", [
										Permission.POLICIES_WAR,
										Permission.POLICIES_CYBERRISK,
									]) && (
										<>
											<Route
												path="policies"
												exact
												element={withSuspense(
													<Policies />
												)}
											/>
											<Route
												path="policies/:id"
												exact
												element={withSuspense(
													<Policy />
												)}
											/>
										</>
									)}
									{checkPermissions("any", [
										Permission.INVOICES_WAR,
										Permission.INVOICES_CYBERRISK,
									]) && (
										<Route
											path="invoices"
											exact
											element={withSuspense(<Invoices />)}
										/>
									)}
									<Route
										path="organisations"
										exact
										element={withSuspense(
											<Organisations />
										)}
									/>
									<Route
										path="organisations/treeview"
										exact
										element={withSuspense(
											<OrganisationsTreeviewPage />
										)}
									/>
									<Route
										path="organisations/:id"
										exact
										element={withSuspense(<Organisation />)}
									/>
									<Route
										path="cyberrisk/*"
										element={withSuspense(<Cyberrisk />)}
									/>
									{/* {(checkPermissions(
										"any",
										Permission.CLAIMS_CYBERRISK,
										false
									) ||
										(user?.uwr &&
											checkPermissions(
												"none",
												Permission.CLAIMS_HANDLER,
												false
											))) && (
										<Route
											path="claims/*"
											element={withSuspense(<Claims />)}
										/>
									)} */}
									{checkPermissions("any", [
										Permission.UPDATE_REQUESTS_ORGANISATIONS,
										Permission.UPDATE_REQUESTS_POLICIES,
										Permission.UPDATE_REQUESTS_VESSELS,
									]) && (
										<>
											<Route
												path="update_requests"
												exact
												element={withSuspense(
													<UpdateRequests />
												)}
											/>
											<Route
												path="update_requests/:id"
												exact
												element={withSuspense(
													<UpdateRequest />
												)}
											/>
											<Route
												path="update_requests/:id/edit"
												exact
												element={withSuspense(
													<UpdateRequest edit />
												)}
											/>
											<Route
												path="update_requests/:type/:id/edit"
												exact
												element={withSuspense(
													<UpdateRequest edit />
												)}
											/>
										</>
									)}
									{checkPermissions(
										"any",
										Permission.USERS_MANAGEMENT
									) && (
										<>
											<Route
												path="users/:id"
												exact
												element={<User />}
											/>
											<Route
												path="users"
												exact
												element={withSuspense(
													<Users />
												)}
											/>
										</>
									)}
									{checkPermissions(
										"none",
										Permission.CIRCULARS,
										false
									) && (
										<Route
											path="circulars"
											exact
											element={withSuspense(
												<Circulars />
											)}
										/>
									)}
									<Route
										path="articles/*"
										element={withSuspense(<Articles />)}
									/>
									<Route path="*" element={<NotFound />} />
								</Routes>
							)}
						</ErrorBoundary>
					</section>
				</section>
			</main>
		</>
	);
}

export default App;
